/**
 * studentRefusal — un refus du serveur, dit à l'élève : au tutoiement, toujours.
 *
 * Le serveur vouvoie (« Votre accès est suspendu… Veuillez régulariser… ») et le parent garde ses
 * phrases. Dans l'espace élève, la phrase se pose côté app (Wajih, 19/10) :
 *  - une phrase connue qui vouvoie a sa version à l'élève (`STUDENT_VOICE`) — le paiement en attente,
 *    et les phrases de l'app que les feuilles partagées avec le parent posent elles-mêmes ;
 *  - une phrase qui ne s'adresse à personne (« Le mot de passe est incorrect. ») se dit telle quelle :
 *    elle ne vouvoie pas, et elle porte ce que l'élève doit savoir ;
 *  - une phrase inconnue qui vouvoie cède la place au repli du geste, écrit au tutoiement par
 *    l'appelant : jamais de « vous » à l'élève, même d'une phrase que l'app n'a pas encore vue ;
 *  - une phrase en anglais (un service tiers, une erreur du réseau : « Network request failed »)
 *    cède aussi la place au repli : l'élève lit le français.
 *
 * Pur : testé seul (`studentRefusal.test.ts`).
 */
import { apiErrorMessage } from "./apiErrorMessage"

/**
 * Les mots qui vouvoient le lecteur. Un mot de la liste suffit. Les bornes de mot sont écrites à la
 * main (lettres accentuées comprises) : `\b` ne connaît que l'ASCII.
 */
const VOUVOIEMENT =
    /(^|[^A-Za-zÀ-ÖØ-öø-ÿ])(vous|votre|vos|veuillez|réessayez|essayez|contactez|vérifiez|régularisez|patientez)(?![A-Za-zÀ-ÖØ-öø-ÿ])/i

/** Les mots qui disent une phrase anglaise : aucun n'est un mot français courant. */
const ENGLISH =
    /(^|[^A-Za-zÀ-ÖØ-öø-ÿ])(the|please|error|failed|failure|could|unable|invalid|your|you|try|again|occurred|request|network|not|was|were|has|have|with|from|this|that|cannot|can't|too|many)(?![A-Za-zÀ-ÖØ-öø-ÿ])/i

const PAYMENT_BLOCKED =
    "Ton accès est suspendu : un paiement est en attente. Il doit être régularisé pour que tu retrouves cette fonctionnalité."

/**
 * Les phrases qui vouvoient, connues, et ce que l'élève lit à leur place. La clé est la phrase telle
 * que le serveur (ou la feuille partagée) l'écrit, espaces et apostrophes normalisés.
 */
const STUDENT_VOICE: Record<string, string> = Object.fromEntries(
    (
        [
            // Le serveur : `LiveSessionController`, `AttendanceFlagController` (un élève bloqué).
            [
                "Votre accès est suspendu en raison d'un paiement en attente. Veuillez régulariser votre situation pour accéder à cette fonctionnalité.",
                PAYMENT_BLOCKED,
            ],
            [
                "Vous ne pouvez plus accepter de proposition de rattrapage moins de 20 minutes avant le début de la session.",
                "Tu ne peux plus accepter de proposition de rattrapage moins de 20 minutes avant le début de la session.",
            ],
            // Les feuilles partagées avec le parent (`attendanceFlagAdapter`, `useParentSessionGesture`,
            // `useLiveSessionsListState`, `useGenerateActivityFlow`).
            [
                "Aucune séance de rattrapage disponible pour le moment. Vous serez notifiés par email lorsqu'il y aura des séances disponibles.",
                "Aucune séance de rattrapage disponible pour le moment. Un e-mail te préviendra lorsqu'il y aura des séances disponibles.",
            ],
            [
                "Vous pouvez tout de même prévenir de l'absence : la séance ne pourra pas être rattrapée.",
                "Tu peux tout de même prévenir de ton absence : la séance ne pourra pas être rattrapée.",
            ],
            [
                "Impossible de charger les créneaux. Veuillez réessayer.",
                "Impossible de charger les créneaux. Réessaie dans un instant.",
            ],
            ["Impossible d'enregistrer. Veuillez réessayer.", "Impossible d'enregistrer. Réessaie dans un instant."],
            ["Impossible d'annuler. Veuillez réessayer.", "Impossible d'annuler. Réessaie dans un instant."],
            // Le succès du message au tuteur (`useLiveSessionsListState`), partagé avec le parent.
            ["Votre message a bien été transmis.", "Ton message a bien été transmis."],
            [
                "Impossible de créer cette activité. Réessayez dans un instant.",
                "Impossible de créer cette activité. Réessaie dans un instant.",
            ],
        ] as const
    ).map(([server, student]) => [normalize(server), student])
)

function normalize(text: string): string {
    return text.replace(/[’‘]/g, "'").replace(/\s+/g, " ").trim()
}

/** Vrai quand la phrase s'adresse au lecteur en le vouvoyant. */
export function vouvoie(text: string): boolean {
    return VOUVOIEMENT.test(text)
}

/** Vrai quand la phrase est en anglais. */
export function inEnglish(text: string): boolean {
    return ENGLISH.test(text)
}

/** Une phrase déjà extraite, dite à l'élève. `fallback` : le repli du geste, au tutoiement. */
export function studentRefusalText(message: string | null | undefined, fallback: string): string {
    if (message == null || message.trim() === "") return fallback
    const known = STUDENT_VOICE[normalize(message)]
    if (known != null) return known
    return vouvoie(message) || inEnglish(message) ? fallback : message
}

/** L'enveloppe d'erreur de l'api, réduite à une phrase (`apiErrorMessage`), dite à l'élève. */
export function studentRefusal(error: unknown, fallback: string): string {
    return studentRefusalText(apiErrorMessage(error, fallback), fallback)
}
